import type { AddressWords } from './address';
import { grandAddress, indicPack, stepNoun, type IndicGrammar, type IndicWords, type UncleKind } from './indic';

/**
 * Gujarati relationship words (PRD 7.2.2), in Gujarati script and in English letters. Beta until reviewed:
 * where families differ (Pappa or Bapuji, Mota Kaka or Mota Bapa) these are common defaults.
 */

const UNCLE_GU: Record<UncleKind, string> = { FB: 'કાકા', FZ: 'ફોઈ', MB: 'મામા', MZ: 'માસી' };
const UNCLE_LATN: Record<UncleKind, string> = { FB: 'Kaka', FZ: 'Foi', MB: 'Mama', MZ: 'Masi' };

const gu: IndicWords = {
  you: 'તમે',
  relative: 'સંબંધી',
  spouse: { m: 'પતિ', f: 'પત્ની' },
  parent: { m: 'પપ્પા', f: 'મમ્મી' },
  child: { m: 'દીકરો', f: 'દીકરી' },
  brother: { elder: 'મોટાભાઈ', younger: 'નાનો ભાઈ', any: 'ભાઈ' },
  sister: { elder: 'મોટી બહેન', younger: 'નાની બહેન', any: 'બહેન' },
  grandF: { m: 'દાદા', f: 'દાદી' },
  grandM: { m: 'નાના', f: 'નાની' },
  greatF: { m: 'પરદાદા', f: 'પરદાદી' },
  greatM: { m: 'પરનાના', f: 'પરનાની' },
  ancestor: 'પૂર્વજ',
  grandchildViaSon: { m: 'પૌત્ર', f: 'પૌત્રી' },
  grandchildViaDaughter: { m: 'દોહિત્ર', f: 'દોહિત્રી' },
  greatGrandchild: { m: 'પ્રપૌત્ર', f: 'પ્રપૌત્રી' },
  descendant: 'વંશજ',
  uncle: { FB: { elder: 'મોટા કાકા', any: 'કાકા' }, FZ: { elder: 'ફોઈ', any: 'ફોઈ' }, MB: { elder: 'મામા', any: 'મામા' }, MZ: { elder: 'માસી', any: 'માસી' } },
  uncleSpouse: { FB: { elder: 'મોટી કાકી', any: 'કાકી' }, FZ: { elder: 'ફુવા', any: 'ફુવા' }, MB: { elder: 'મામી', any: 'મામી' }, MZ: { elder: 'માસા', any: 'માસા' } },
  cousin: (kind, sex) => {
    const b = UNCLE_GU[kind];
    return sex === 'f' ? `${b}ની દીકરી` : sex === 'm' ? `${b}નો દીકરો` : `${b}નો દીકરો/${b}ની દીકરી`;
  },
  distantCousin: { m: 'દૂરના ભાઈ', f: 'દૂરની બહેન' },
  nephewViaBrother: { m: 'ભત્રીજો', f: 'ભત્રીજી' },
  nephewViaSister: { m: 'ભાણેજ', f: 'ભાણી' },
  parentInLaw: { m: 'સસરા', f: 'સાસુ' },
  grandparentInLaw: { m: 'વડસસરા', f: 'વડસાસુ' },
  spouseUncle: (kind) => ({ FB: 'કાકાસસરા', FZ: 'ફોઈસાસુ', MB: 'મામાસસરા', MZ: 'માસીસાસુ' })[kind],
  husbandBrother: { elder: 'જેઠ', younger: 'દિયર', any: 'જેઠ/દિયર' },
  husbandSister: 'નણંદ',
  wifeBrother: 'સાળો',
  wifeSister: 'સાળી',
  husbandBrotherWife: { elder: 'જેઠાણી', younger: 'દેરાણી', any: 'જેઠાણી/દેરાણી' },
  husbandSisterHusband: 'નણદોઈ',
  wifeSisterHusband: 'સાઢુ',
  wifeBrotherWife: 'સાળાની પત્ની',
  brotherWife: 'ભાભી',
  sisterHusband: { elder: 'બનેવી', younger: 'બનેવી', any: 'બનેવી' },
  childInLaw: { m: 'જમાઈ', f: 'વહુ' },
  coParentInLaw: { m: 'વેવાઈ', f: 'વેવાણ' },
  stepParent: { m: 'ઓરમાન બાપ', f: 'ઓરમાન મા' },
  stepChild: { m: 'ઓરમાન દીકરો', f: 'ઓરમાન દીકરી' },
  stepSibling: { m: 'ઓરમાન ભાઈ', f: 'ઓરમાન બહેન' },
};

const guLatn: IndicWords = {
  you: 'Tame',
  relative: 'Sambandhi',
  spouse: { m: 'Pati', f: 'Patni' },
  parent: { m: 'Pappa', f: 'Mummy' },
  child: { m: 'Dikro', f: 'Dikri' },
  brother: { elder: 'Mota Bhai', younger: 'Nano Bhai', any: 'Bhai' },
  sister: { elder: 'Moti Ben', younger: 'Nani Ben', any: 'Ben' },
  grandF: { m: 'Dada', f: 'Dadi' },
  grandM: { m: 'Nana', f: 'Nani' },
  greatF: { m: 'Pardada', f: 'Pardadi' },
  greatM: { m: 'Parnana', f: 'Parnani' },
  ancestor: 'Purvaj',
  grandchildViaSon: { m: 'Pautra', f: 'Pautri' },
  grandchildViaDaughter: { m: 'Dohitra', f: 'Dohitri' },
  greatGrandchild: { m: 'Prapautra', f: 'Prapautri' },
  descendant: 'Vanshaj',
  uncle: { FB: { elder: 'Mota Kaka', any: 'Kaka' }, FZ: { elder: 'Foi', any: 'Foi' }, MB: { elder: 'Mama', any: 'Mama' }, MZ: { elder: 'Masi', any: 'Masi' } },
  uncleSpouse: { FB: { elder: 'Moti Kaki', any: 'Kaki' }, FZ: { elder: 'Fuva', any: 'Fuva' }, MB: { elder: 'Mami', any: 'Mami' }, MZ: { elder: 'Masa', any: 'Masa' } },
  cousin: (kind, sex) => {
    const b = UNCLE_LATN[kind];
    return sex === 'f' ? `${b} ni Dikri` : sex === 'm' ? `${b} no Dikro` : `${b} no Dikro/${b} ni Dikri`;
  },
  distantCousin: { m: 'Dur na Bhai', f: 'Dur ni Ben' },
  nephewViaBrother: { m: 'Bhatrijo', f: 'Bhatriji' },
  nephewViaSister: { m: 'Bhanej', f: 'Bhani' },
  parentInLaw: { m: 'Sasra', f: 'Sasu' },
  grandparentInLaw: { m: 'Vadsasra', f: 'Vadsasu' },
  spouseUncle: (kind) => ({ FB: 'Kaka Sasra', FZ: 'Foi Sasu', MB: 'Mama Sasra', MZ: 'Masi Sasu' })[kind],
  husbandBrother: { elder: 'Jeth', younger: 'Diyar', any: 'Jeth/Diyar' },
  husbandSister: 'Nanand',
  wifeBrother: 'Salo',
  wifeSister: 'Sali',
  husbandBrotherWife: { elder: 'Jethani', younger: 'Derani', any: 'Jethani/Derani' },
  husbandSisterHusband: 'Nandoi',
  wifeSisterHusband: 'Sadhu',
  wifeBrotherWife: 'Sala ni Patni',
  brotherWife: 'Bhabhi',
  sisterHusband: { elder: 'Banevi', younger: 'Banevi', any: 'Banevi' },
  childInLaw: { m: 'Jamai', f: 'Vahu' },
  coParentInLaw: { m: 'Vevai', f: 'Vevan' },
  stepParent: { m: 'Orman Bap', f: 'Orman Ma' },
  stepChild: { m: 'Orman Dikro', f: 'Orman Dikri' },
  stepSibling: { m: 'Orman Bhai', f: 'Orman Ben' },
};

/** Possessives agree with the next noun: no (m), ni (f), na (honorific, or before another possessive). */
function grammar(latin: boolean): IndicGrammar {
  const w = (word: string, oblique = word, honorific = false) => ({ word, oblique, honorific });
  const forms = latin
    ? {
        parent: { m: w('pappa', 'pappa', true), f: w('mummy') },
        child: { m: w('dikro', 'dikra'), f: w('dikri') },
        sibling: { m: w('bhai'), f: w('ben') },
        spouse: { m: w('pati', 'pati', true), f: w('patni') },
      }
    : {
        parent: { m: w('પપ્પા', 'પપ્પા', true), f: w('મમ્મી') },
        child: { m: w('દીકરો', 'દીકરા'), f: w('દીકરી') },
        sibling: { m: w('ભાઈ'), f: w('બહેન') },
        spouse: { m: w('પતિ', 'પતિ', true), f: w('પત્ની') },
      };
  const [no, ni, na] = latin ? ['no', 'ni', 'na'] : ['નો', 'ની', 'ના'];
  return {
    noun: (step) => stepNoun(step, forms),
    your: (fem) => (latin ? (fem ? 'tamari' : 'tamara') : fem ? 'તમારી' : 'તમારા'),
    of: (fem, last, honorific) => (fem ? ni : honorific || !last ? na : no),
    attach: !latin,
    generation: (delta) => {
      if (delta === 0) return latin ? 'Tamari pedhi' : 'તમારી પેઢી';
      if (delta === -1) return latin ? 'Mata-pita' : 'માતા-પિતા';
      if (delta === 1) return latin ? 'Santano' : 'સંતાનો';
      const n = Math.abs(delta);
      if (latin) return `${n} pedhi ${delta < 0 ? 'upar' : 'niche'}`;
      return `${n} પેઢી ${delta < 0 ? 'ઉપર' : 'નીચે'}`;
    },
  };
}

/**
 * Forms of address (PRD 7.2.4). Elders get a kin term with the name joined in front ("Rohanbhai", "Jayaben",
 * "Rameshkaka", "Jayafoi"); younger relatives are called by name (Mistry 1982; Pocock 1972, App. I). Motabhai and
 * Motiben are for the eldest; an elder brother's wife is "Komal Bhabhi". Sources: docs/kinship-languages-plan.md
 * 16.1 and 16.7.
 */
const guAddress: AddressWords = {
  nameScript: /^\p{Script=Gujarati}+$/u,
  parent: { m: ['પપ્પા', 'બાપુજી'], f: ['મમ્મી'] },
  grandparent: grandAddress([['દાદા', 'દાદાજી'], ['દાદી', 'દાદીમા'], ['નાના', 'નાનાજી'], ['નાની', 'નાનીમા']], [['પરદાદા'], ['પરદાદી'], ['પરનાના'], ['પરનાની']]),
  elder: { m: ['{name}ભાઈ', 'મોટાભાઈ'], f: ['{name}બેન', 'બેન', 'મોટીબેન', 'દીદી'] },
  uncle: {
    FB: { elder: ['મોટાબાપા', 'મોટા કાકા', '{name}કાકા'], any: ['કાકા', '{name}કાકા'] },
    FZ: { any: ['ફોઈ', 'ફોઈબા', '{name}ફોઈ'] },
    MB: { any: ['મામા', '{name}મામા'] },
    MZ: { any: ['માસી', 'માસીબા', '{name}માસી'] },
  },
  uncleSpouse: {
    FB: { elder: ['મોટીમા', 'ભાભુ', 'મોટી કાકી'], any: ['કાકી', '{name}કાકી'] },
    FZ: { any: ['ફુવા', 'ફુવાજી', '{name}ફુવાજી'] },
    MB: { any: ['મામી'] },
    MZ: { any: ['માસા', 'માસાજી', '{name}માસાજી'] },
  },
  parentInLaw: { m: ['પપ્પા', 'બાપુજી'], f: ['મમ્મી'] },
  elderInLaw: (sex) => (sex === 'f' ? ['{name}બેન'] : sex === 'm' ? ['{name}ભાઈ'] : ['{name}ભાઈ', '{name}બેન']),
  elderSiblingSpouse: { m: ['{name} જીજાજી', 'જીજાજી'], f: ['{name} ભાભી', 'ભાભી', 'ભાભુ'] },
  coSiblingInLaw: (sex, spouseSex) => (sex === 'f' && spouseSex === 'm' ? ['દીદી', '{name}બેન'] : []),
  coParentInLaw: { m: ['વેવાઈ'], f: ['વેવાણ'] },
};
const guLatnAddress: AddressWords = {
  parent: { m: ['Pappa', 'Bapuji'], f: ['Mummy'] },
  grandparent: grandAddress([['Dada', 'Dadaji'], ['Dadi', 'Dadima'], ['Nana', 'Nanaji'], ['Nani', 'Nanima']], [['Pardada'], ['Pardadi'], ['Parnana'], ['Parnani']]),
  elder: { m: ['{name}bhai', 'Motabhai'], f: ['{name}ben', 'Ben', 'Motiben', 'Didi'] },
  uncle: {
    FB: { elder: ['Motabapa', 'Mota Kaka', '{name}kaka'], any: ['Kaka', '{name}kaka'] },
    FZ: { any: ['Foi', 'Foiba', '{name}foi'] },
    MB: { any: ['Mama', '{name}mama'] },
    MZ: { any: ['Masi', 'Masiba', '{name}masi'] },
  },
  uncleSpouse: {
    FB: { elder: ['Motima', 'Bhabhu', 'Moti Kaki'], any: ['Kaki', '{name}kaki'] },
    FZ: { any: ['Fuva', 'Fuvaji', '{name}fuvaji'] },
    MB: { any: ['Mami'] },
    MZ: { any: ['Masa', 'Masaji', '{name}masaji'] },
  },
  parentInLaw: { m: ['Pappa', 'Bapuji'], f: ['Mummy'] },
  elderInLaw: (sex) => (sex === 'f' ? ['{name}ben'] : sex === 'm' ? ['{name}bhai'] : ['{name}bhai', '{name}ben']),
  elderSiblingSpouse: { m: ['{name} Jijaji', 'Jijaji'], f: ['{name} Bhabhi', 'Bhabhi', 'Bhabhu'] },
  coSiblingInLaw: (sex, spouseSex) => (sex === 'f' && spouseSex === 'm' ? ['Didi', '{name}ben'] : []),
  coParentInLaw: { m: ['Vevai', '{name}bhai'], f: ['Vevan', '{name}ben'] },
};

export const guPack = indicPack({ id: 'gu', name: 'ગુજરાતી', lang: 'gu' }, gu, grammar(false), false, guAddress);
export const guLatnPack = indicPack({ id: 'gu-Latn', name: 'Gujarati (kaka, masi)', lang: 'gu-Latn' }, guLatn, grammar(true), true, guLatnAddress);
